import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { researchService, Report } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface PendingResearch {
  id: string;
  topic: string;
  startTime: string;
}

const ReportsList: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<string | null>(null);
  const [pendingResearch, setPendingResearch] = useState<PendingResearch[]>([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for notification from navigation state
  useEffect(() => {
    if (location.state && location.state.notification) {
      setNotification(location.state.notification);
      // Clear the navigation state after reading it
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Load pending research from localStorage
  useEffect(() => {
    const storedPending = localStorage.getItem('pendingResearch');
    if (storedPending) {
      try {
        const parsed = JSON.parse(storedPending);
        setPendingResearch(parsed);
      } catch (e) {
        console.error('Error parsing pending research:', e);
        localStorage.removeItem('pendingResearch');
      }
    }
  }, []);

  // Check status of pending research
  useEffect(() => {
    const checkPendingResearch = async () => {
      if (pendingResearch.length === 0 || !user) return;
      
      const updatedPending = [...pendingResearch];
      let pendingChanged = false;
      
      for (let i = 0; i < updatedPending.length; i++) {
        try {
          const research = updatedPending[i];
          const status = await researchService.getResearchStatus(research.id);
          
          if (status.status === 'completed') {
            // Research is complete, remove from pending
            updatedPending.splice(i, 1);
            i--; // Adjust index after removal
            pendingChanged = true;
            
            // Show notification that research is complete
            setNotification(`Research on "${research.topic}" is now complete! View it in your reports.`);
            
            // Force refresh reports list to include the new report
            setLoading(true);
          }
        } catch (err) {
          console.error('Error checking research status:', err);
        }
      }
      
      if (pendingChanged) {
        localStorage.setItem('pendingResearch', JSON.stringify(updatedPending));
        setPendingResearch(updatedPending);
      }
    };
    
    // Check immediately and then set up interval
    checkPendingResearch();
    const interval = setInterval(checkPendingResearch, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
  }, [pendingResearch, user]);

  useEffect(() => {
    // Only fetch reports if user is authenticated
    if (!user) {
      console.log('User not authenticated, redirecting to login');
      navigate('/login');
      return;
    }

    const fetchReports = async () => {
      try {
        setLoading(true);
        setError(null); // Clear any previous errors
        
        console.log('Fetching reports for user:', user.uid);
        const data = await researchService.getAllReports();
        console.log('Reports fetched successfully:', data.length);
        setReports(data);
      } catch (err: any) {
        console.error('Error fetching reports:', err);
        
        // Check if it's an authentication error
        if (err.response?.status === 401) {
          setError('Authentication error. Please try logging in again.');
          // Redirect to login after a short delay
          setTimeout(() => navigate('/login'), 2000);
        } else {
          setError(err.response?.data?.detail || 'Failed to fetch reports');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [user, navigate]);

  const handleDelete = async (reportId: string) => {
    if (window.confirm('Are you sure you want to delete this report?')) {
      try {
        setLoading(true);
        console.log('Deleting report:', reportId);
        await researchService.deleteReport(reportId);
        console.log('Report deleted successfully');
        setReports(reports.filter(report => report.id !== reportId));
        setLoading(false);
      } catch (err: any) {
        console.error('Error deleting report:', err);
        setLoading(false);
        
        // Check if it's an authentication error
        if (err.response?.status === 401) {
          setError('Authentication error. Please try logging in again.');
          // Redirect to login after a short delay
          setTimeout(() => navigate('/login'), 2000);
        } else {
          setError(err.response?.data?.detail || 'Failed to delete report');
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
        <div className="flex space-x-4">
          <button
            onClick={() => {
              setError(null);
              setLoading(true);
              researchService.getAllReports()
                .then(data => {
                  setReports(data);
                  setLoading(false);
                })
                .catch(err => {
                  setError(err.response?.data?.detail || 'Failed to fetch reports');
                  setLoading(false);
                });
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Try Again
          </button>
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-white shadow-xl rounded-lg p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-6">Research Reports</h1>
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">No reports found</p>
            <Link 
              to="/" 
              className="inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Start New Research
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
      {notification && (
        <div className="mb-6 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-md" role="alert">
          <div className="flex items-center">
            <svg className="h-6 w-6 text-green-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="font-bold">Notification</p>
              <p className="text-sm">{notification}</p>
            </div>
            <button 
              onClick={() => setNotification(null)}
              className="ml-auto text-green-700 hover:text-green-900"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      {pendingResearch.length > 0 && (
        <div className="mb-6 bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 rounded shadow-md" role="alert">
          <div className="flex items-center">
            <svg className="h-6 w-6 text-blue-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="font-bold">Research in Progress</p>
              <ul className="text-sm list-disc ml-5 mt-1">
                {pendingResearch.map(research => (
                  <li key={research.id}>
                    "{research.topic}" - started {new Date(research.startTime).toLocaleTimeString()}
                    <Link 
                      to={`/research/${research.id}`}
                      className="ml-2 text-blue-800 hover:underline"
                    >
                      View Status
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-white shadow-xl rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Research Reports</h1>
          <Link 
            to="/" 
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            New Research
          </Link>
        </div>
        
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {reports.map(report => (
            <div 
              key={report.id} 
              className="bg-white border border-gray-200 rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="p-5">
                <h2 className="text-xl font-bold tracking-tight text-gray-900 mb-2 line-clamp-2">
                  {report.outline?.title || `Research on ${report.topic}`}
                </h2>
                <p className="text-sm text-gray-500 mb-3">
                  {new Date(report.created_at).toLocaleDateString()}
                </p>
                <p className="text-gray-700 mb-4 line-clamp-3">
                  {report.summary || 'No summary available'}
                </p>
                <div className="flex justify-between items-center">
                  <Link
                    to={`/reports/${report.id}`}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-center text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    Read Report
                  </Link>
                  <button
                    onClick={() => handleDelete(report.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReportsList;
